import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';
import { getBulkPresence } from '../../shared/redis';

export async function getOrCreateDM(userIdA: string, userIdB: string) {
  const isSelfChat = userIdA === userIdB;

  if (isSelfChat) {
    // Find all single-member self-conversations for this user
    const directConvs = await prisma.conversation.findMany({
      where: {
        type: 'direct',
        members: {
          some: { userId: userIdA }
        }
      },
      include: {
        members: {
          include: {
            user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } }
          }
        }
      },
      orderBy: { createdAt: 'asc' }
    });

    const selfConvs = directConvs.filter(c => c.members.length === 1 && c.members[0].userId === userIdA);

    if (selfConvs.length > 0) {
      if (selfConvs.length > 1) {
        const [keepConv, ...extraConvs] = selfConvs;
        const extraIds = extraConvs.map(c => c.id);
        (async () => {
          try {
            await prisma.message.updateMany({
              where: { conversationId: { in: extraIds } },
              data: { conversationId: keepConv.id }
            });
            await prisma.conversationMember.deleteMany({
              where: { conversationId: { in: extraIds } }
            });
            await prisma.conversation.deleteMany({
              where: { id: { in: extraIds } }
            });
          } catch (e) {
            console.warn('Error cleaning up duplicate self-chats:', e);
          }
        })();
        return keepConv;
      }
      return selfConvs[0];
    }

    return prisma.conversation.create({
      data: {
        type: 'direct',
        createdBy: userIdA,
        members: { create: [{ userId: userIdA }] },
      },
      include: { members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } } } } },
    });
  }

  // Check if DM already exists with exactly these two members
  const directConvs = await prisma.conversation.findMany({
    where: {
      type: 'direct',
      AND: [
        { members: { some: { userId: userIdA } } },
        { members: { some: { userId: userIdB } } }
      ]
    },
    include: {
      members: {
        include: {
          user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } }
        }
      }
    },
    orderBy: { createdAt: 'asc' }
  });

  const matchingConvs = directConvs.filter(
    c => c.members.length === 2 &&
         c.members.some(m => m.userId === userIdA) &&
         c.members.some(m => m.userId === userIdB)
  );

  if (matchingConvs.length > 0) {
    if (matchingConvs.length > 1) {
      const [keepConv, ...extraConvs] = matchingConvs;
      const extraIds = extraConvs.map(c => c.id);
      (async () => {
        try {
          await prisma.message.updateMany({
            where: { conversationId: { in: extraIds } },
            data: { conversationId: keepConv.id }
          });
          await prisma.conversationMember.deleteMany({
            where: { conversationId: { in: extraIds } }
          });
          await prisma.conversation.deleteMany({
            where: { id: { in: extraIds } }
          });
        } catch (e) {
          console.warn('Error cleaning up duplicate DMs:', e);
        }
      })();
      return keepConv;
    }
    return matchingConvs[0];
  }

  return prisma.conversation.create({
    data: {
      type: 'direct',
      createdBy: userIdA,
      members: { create: [{ userId: userIdA }, { userId: userIdB }] },
    },
    include: { members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } } } } },
  });
}

export async function createGroupConversation(creatorId: string, memberIds: string[], name?: string) {
  const allMembers = Array.from(new Set([creatorId, ...memberIds]));
  return prisma.conversation.create({
    data: {
      type: 'group',
      name,
      createdBy: creatorId,
      members: { create: allMembers.map((userId) => ({ userId })) },
    },
    include: { members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } } } } },
  });
}

import { Prisma } from '@prisma/client';

export async function listConversations(userId: string, limit: number = 50, cursor?: string) {
  const where: any = { members: { some: { userId } } };

  // Fetch current user visibility policy
  const currentUser = await prisma.user.findUnique({
    where: { id: userId },
    include: {
      policy: true,
      visibleUsers: { select: { id: true } },
      department: true,
      roles: { include: { role: true } }
    }
  });

  const isAdmin = currentUser?.roles?.some((ur: any) => ur.role.name === 'admin' || ur.role.name === 'super_admin');

  const conversations = await prisma.conversation.findMany({
    where,
    take: limit,
    ...(cursor ? { skip: 1, cursor: { id: cursor } } : {}),
    include: {
      members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, presence: true, email: true, company: true, department: true } } } },
      messages: {
        where: { isDeleted: false },
        orderBy: { createdAt: 'desc' },
        take: 1,
        select: {
          id: true,
          content: true,
          createdAt: true,
          senderId: true,
          metadata: true,
          sender: { select: { id: true, displayName: true } },
        },
      },
    },
    orderBy: { createdAt: 'desc' },
  });

  if (conversations.length === 0) {
    return [];
  }

  // Filter conversations based on policy visibility rules for 1-on-1 direct chats
  const filteredConversations = conversations.filter(conv => {
    const isSelfChat = conv.members.every(m => m.userId === userId) && conv.members.length === 1;
    if (isSelfChat || conv.type === 'group' || isAdmin) return true;

    const otherMember = conv.members.find(m => m.userId !== userId);
    if (!otherMember) return true;

    if (currentUser?.policy) {
      const p = currentUser.policy as any;
      const compIds = Array.isArray(p.companyIds) ? (p.companyIds as string[]).filter(Boolean) : [];
      const deptIds = Array.isArray(p.departmentIds) ? (p.departmentIds as string[]).filter(Boolean) : [];
      const uIds = Array.isArray(p.userIds) ? (p.userIds as string[]).filter(Boolean) : [];

      const otherUser = otherMember.user;
      const isAllowed =
        uIds.includes(otherMember.userId) ||
        (otherUser?.company?.id && compIds.includes(otherUser.company.id)) ||
        (otherUser?.department?.id && deptIds.includes(otherUser.department.id));

      return isAllowed;
    }

    if (currentUser?.visibleUsers && currentUser.visibleUsers.length > 0) {
      const visibleIds = currentUser.visibleUsers.map((u: any) => u.id);
      return visibleIds.includes(otherMember.userId);
    }

    return true;
  });

  // Deduplicate conversations: only 1 self-chat and at most 1 direct conversation per peer user
  const seenDirectPeers = new Set<string>();
  let hasSelfChat = false;
  const deduplicatedConversations: typeof filteredConversations = [];
  const redundantConvIds: string[] = [];

  for (const conv of filteredConversations) {
    if (conv.type === 'group') {
      deduplicatedConversations.push(conv);
      continue;
    }

    const isSelf = conv.members.every(m => m.userId === userId) && conv.members.length === 1;
    if (isSelf) {
      if (!hasSelfChat) {
        hasSelfChat = true;
        deduplicatedConversations.push(conv);
      } else {
        redundantConvIds.push(conv.id);
      }
      continue;
    }

    const otherMember = conv.members.find(m => m.userId !== userId);
    const peerId = otherMember?.userId || otherMember?.user?.id;
    if (peerId) {
      if (!seenDirectPeers.has(peerId)) {
        seenDirectPeers.add(peerId);
        deduplicatedConversations.push(conv);
      } else {
        redundantConvIds.push(conv.id);
      }
    } else {
      deduplicatedConversations.push(conv);
    }
  }

  // Clean up redundant duplicate conversations in background
  if (redundantConvIds.length > 0) {
    (async () => {
      try {
        await prisma.message.deleteMany({
          where: { conversationId: { in: redundantConvIds } }
        });
        await prisma.conversationMember.deleteMany({
          where: { conversationId: { in: redundantConvIds } }
        });
        await prisma.conversation.deleteMany({
          where: { id: { in: redundantConvIds } }
        });
      } catch (err) {
        console.warn('Error cleaning redundant conversations:', err);
      }
    })();
  }

  const allUserIds = Array.from(new Set(deduplicatedConversations.flatMap(c => c.members.map(m => m.userId))));
  const presenceMap = await getBulkPresence(allUserIds);

  const convIds = deduplicatedConversations.map(c => c.id);
  const unreadCountsRaw: { conversationId: string; unreadCount: number }[] = convIds.length > 0 ? await prisma.$queryRaw`
    SELECT 
      m."conversationId",
      COUNT(m.id)::int as "unreadCount"
    FROM messages m
    JOIN conversation_members cm ON cm."conversationId" = m."conversationId" AND cm."userId" = ${userId}
    WHERE m."conversationId" IN (${Prisma.join(convIds)})
      AND m."isDeleted" = false
      AND (m."senderId" != ${userId} OR (SELECT COUNT(*) FROM conversation_members WHERE "conversationId" = m."conversationId") = 1)
      AND m."createdAt" > cm."lastReadAt"
    GROUP BY m."conversationId"
  ` : [];

  const unreadMap: Record<string, number> = {};
  unreadCountsRaw.forEach((row) => {
    unreadMap[row.conversationId] = row.unreadCount;
  });

  return filteredConversations.map((conv) => {
    conv.members.forEach(m => {
      if (m.user) {
        m.user.presence = (presenceMap[m.userId] || 'offline') as any;
      }
    });

    const myMembership = conv.members.find((m) => m.userId === userId);
    const lastReadAt = myMembership?.lastReadAt ?? new Date(0);
    const isSelfChat = conv.members.every(m => m.userId === userId) && conv.members.length === 1;
    const unreadCount = unreadMap[conv.id] || 0;

    return { ...conv, unreadCount, myLastReadAt: lastReadAt, isSelfChat };
  });
}
